/**
 * Belege im Kontor — Vokabular, Ableitungen, Formatierer.
 *
 * Das Kategorien-Vokabular ist 1:1 aus der Desktop-App uebernommen
 * (apps/vimaana-desktop/src/lib/belegKategorien.ts). Beide Masken schreiben in
 * dieselbe Postgres-Tabelle `beleg`; liefen die Listen auseinander, saehe
 * derselbe Beleg in Desktop und Web unterschiedlich klassifiziert aus.
 * Serverseitig traegt `scanflow.BELEG_TYPEN` beide Vokabulare.
 */

export type Beleg = {
  id: string;
  source: string;
  lieferant: string;
  /** Rechnungsnummer des Lieferanten — staerkster Dublettenanker. */
  rechnungsnummer: string;
  datum: string;
  /** Zahlungsziel; leer = nicht erfasst. Sortiert den Ausgangskorb. */
  faelligkeit: string;
  netto: number;
  bruttobetrag: number;
  mwst_betrag: number;
  /** Skonto (Migration 184): Satz und Zahlbetrag laut Rechnung oder aus der Zahlung. */
  skonto_prozent?: number | null;
  skonto_betrag?: number | null;
  skonto_bis?: string;
  waehrung: string;
  beleg_typ: string;
  notiz: string;
  belegpfad: string;
  projekt_id: string;
  projekt_title: string;
  status: string;
  ausgabe_typ: string;
  zahlungsart: string;
  zahlungsweg: string;
  /** Inhaber hinter Privatkauf (Entnahme) oder privater Auslage (Einlage). */
  inhaber_id: string;
  /** Firma hat bezahlt, die Sache ist privat → Entnahme beim Inhaber. */
  is_privatentnahme: boolean;
  helfer_id: string;
  /** Team-Rechnung: Stundenabrechnung oder projektbezogene Pauschalabrechnung. */
  helfer_rechnung_art: "" | "stunden" | "pauschale";
  lieferant_firma_id: string;
  kontobewegung_id: string;
  is_bezahlt: boolean;
  /** Vom Nutzer in den Ausgangskorb uebernommen (Migration 066). */
  im_zahllauf: boolean;
  /** Am Beleg erkannte Empfaengerbank — Grundlage fuer „IBAN uebernehmen". */
  erkannte_iban: string;
  erkannter_kontoinhaber: string;
  ueberweisung_status: string;
  an_steuerberater_versendet_at: string | null;
  ki_beschreibung: string;
  scan_warnung: string;
  created_at: string;
  updated_at: string;
  /** Kostenaufteilung auf mehrere Projekte. Leer = nur `projekt_id`. */
  aufteilung: BelegAufteilungZeile[];
  /**
   * Ein Inhaber hat privat gezahlt (bar oder vom Privatkonto) — heute nur bei
   * Helferrechnungen, über die Auszahlung im Lohnkonto. null = die GbR zahlt.
   */
  privat_zahlung?: BelegPrivatZahlung | null;
};

export type BelegPrivatZahlung = {
  zahler: string;
  art: "ueberweisung" | "bar";
  nachweis_nummer: string;
  nachweis_versendet: boolean;
};

/**
 * Ein Anteil eines Belegs an einem Projekt.
 *
 * Ein Einkauf wird selten auf genau einer Baustelle verbaut. Statt den Beleg zu
 * duplizieren, traegt jede Zeile einen Nettoanteil; die Summe muss dem
 * Beleg-Netto entsprechen (der Server weist alles andere ab).
 */
export type BelegAufteilungZeile = {
  projekt_id: string;
  projekt_title: string;
  kunde_name: string;
  netto: number;
};

export type BelegListe = { total: number; items: Beleg[] };

/** Moegliche Dublette zu einem Beleg. */
export type Dublette = {
  id: string;
  lieferant: string;
  datum: string;
  bruttobetrag: number;
  status: string;
  grund: "rechnungsnummer" | "hash" | "felder" | "betrag_datum" | string;
};

export const DUBLETTEN_GRUND: Record<string, string> = {
  rechnungsnummer: "gleiche Rechnungsnummer beim selben Lieferanten",
  hash: "dieselbe Datei",
  felder: "gleicher Lieferant, Datum und Betrag",
  betrag_datum: "gleiches Datum und gleicher Betrag",
};

/** Bankzeile mit demselben Betrag wie der Beleg — Vorschlag zum Verknuepfen. */
export type PassendeBankzeile = {
  id: string;
  buchungsdatum: string;
  betrag: number;
  verwendungszweck: string;
  gegenpartei_name: string;
  /** true = an dieser Zeile haengt schon ein anderer Beleg. */
  hat_beleg: boolean;
  /** Stand wie im Konto-Reiter (kontor._BEZUGS_STATUS_SQL). */
  bezugs_status?: "fehlt" | "vorschlag" | "zugeordnet" | "abgeschlossen";
  /** Diese Zeile hat der automatische Abgleich für diesen Beleg vorgeschlagen. */
  vorschlag?: boolean;
  /** Betrag passt nur mit Skonto — der Satz in Prozent (Migration 184). */
  skonto_satz?: number | null;
  /** Woran die Zeile schon hängt: „Beleg", „ohne Beleg abgeschlossen", „Lohn, Rechnung …". */
  belegt_durch?: string;
};

export type BelegPatch = {
  lieferant?: string;
  datum?: string;
  faelligkeit?: string;
  is_bezahlt?: boolean;
  netto?: number | null;
  bruttobetrag?: number | null;
  mwst_betrag?: number | null;
  beleg_typ?: string;
  ausgabe_typ?: string;
  projekt_id?: string | null;
  helfer_id?: string | null;
  notiz?: string;
  status?: string;
  lieferant_firma_id?: string | null;
  im_zahllauf?: boolean;
  rechnungsnummer?: string | null;
  zahlungsweg?: "gbr" | "inhaber";
  inhaber_id?: string;
  is_privatentnahme?: boolean;
};

/** Projekt-Vorschlag im Picker — mit Kunde, weil Projekttitel sich wiederholen. */
export type ProjektOption = { id: string; title: string; kunde_name?: string };

/** Eine Pauschale des Helfers, wie sie die Belegverarbeitung zeigt (Migration 188). */
export type BelegPauschale = {
  id: string;
  projekt_id: string;
  projekt_title: string;
  bezeichnung: string;
  betrag: number;
  datum: string;
  freigegeben: boolean;
  /** Mit DIESER Rechnung abgerechnet. */
  verknuepft: boolean;
};

export type BelegPauschalenAntwort = {
  beleg_betrag: number;
  /** Ältere Pauschalen ohne Rechnung gelten nicht als offen. */
  offen_ab: string;
  pauschalen: BelegPauschale[];
};

/** „Hangbefestigung · Schneider" statt nur „Hangbefestigung". */
export function projektLabel(p: { title: string; kunde_name?: string }): string {
  return p.kunde_name ? `${p.title} · ${p.kunde_name}` : p.title;
}

/* ══ Kategorien ═════════════════════════════════════════════════════════ */

/**
 * Kategorien der Belegverarbeitung (Benjamin, 25.09.2026): „Lieferantenrechnung
 * und Quittung/Betrieb sind zu ungenau — eigentlich beides Lieferantenrechnungen,
 * die erste mit Projektbezug, die zweite ohne." Daraus Projektkosten und
 * Betriebskosten. „Angebot" ist raus — Angebote kommen im Projekt über Mails an,
 * nicht durch die Belegverarbeitung. „Bewirtung/Verpflegung" heißt Bewirtung.
 *
 * Die Kategorie ist die Kostenart (`ausgabe_typ`). `beleg_typ` bleibt die
 * Dokumentart (Rechnung, Kassenbon …) und wird nur gesetzt, wo die Kategorie
 * eine bestimmte verlangt (`belegTyp`) oder die alte nicht mehr passt.
 */
export const BELEG_TYPEN = [
  { id: "projekt", label: "Projektkosten", sub: "Material, Pflanzen, Fremdleistung — mit Projekt", ausgabe: "lieferant", belegTyp: "" },
  { id: "betrieb", label: "Betriebskosten", sub: "Sprit, Werkzeug, Büro — ohne Projekt", ausgabe: "betrieb", belegTyp: "" },
  { id: "helfer", label: "Helferrechnung", sub: "Lohnabrechnung von Helfern", ausgabe: "helfer", belegTyp: "helferrechnung" },
  { id: "bewirtung", label: "Bewirtung", sub: "Bewirtungsbeleg, Verpflegung", ausgabe: "verpflegung", belegTyp: "" },
  { id: "eigenbeleg", label: "Eigenbeleg", sub: "Interne Kosten ohne fremden Beleg", ausgabe: "", belegTyp: "eigenbeleg" },
] as const;

export type BelegTyp = (typeof BELEG_TYPEN)[number];

/** Dokumentarten, die zu keiner Kostenkategorie mehr passen und beim Wechsel ersetzt werden. */
const UNPASSENDE_DOKUMENTART = new Set<string>(["", "helferrechnung", "eigenbeleg", "angebot", "lieferschein", "sonstiges", "vertragsunterlage"]);

const DOKUMENTART: Record<string, string> = {
  rechnung: "Rechnung", kassenbeleg: "Kassenbeleg", kassenbon: "Kassenbon", quittung: "Quittung",
  gutschrift: "Gutschrift", bewirtungsbeleg: "Bewirtungsbeleg", lieferschein: "Lieferschein",
  eigenbeleg: "Eigenbeleg", helferrechnung: "Helferrechnung", vertragsunterlage: "Vertragsunterlage",
};

/**
 * Aktive Kategorie eines Belegs. Die Kostenart `ausgabe_typ` gewinnt; ohne sie
 * entscheidet die Dokumentart, wo sie eindeutig ist. Sonst `undefined` — die
 * Auswahl zeigt dann „— wählen —", statt eine Kategorie vorzutäuschen.
 */
export function aktiveBelegKategorie(beleg: Beleg): BelegTyp | undefined {
  const perAusgabe = BELEG_TYPEN.find((t) => t.ausgabe !== "" && t.ausgabe === (beleg.ausgabe_typ || ""));
  if (perAusgabe) return perAusgabe;
  const typ = beleg.beleg_typ || "";
  if (typ === "helferrechnung") return BELEG_TYPEN[2];
  if (typ === "bewirtungsbeleg") return BELEG_TYPEN[3];
  if (typ === "eigenbeleg") return BELEG_TYPEN[4];
  if (typ === "quittung") return BELEG_TYPEN[1];
  return undefined;
}

/** Was beim Wechsel auf Kategorie `k` gespeichert wird. */
export function kategoriePatch(beleg: Beleg, k: BelegTyp): BelegPatch {
  const p: BelegPatch = { ausgabe_typ: k.ausgabe };
  if (k.belegTyp) p.beleg_typ = k.belegTyp;
  else if (UNPASSENDE_DOKUMENTART.has(beleg.beleg_typ || "")) p.beleg_typ = "rechnung";
  if (k.ausgabe !== "helfer") p.helfer_id = null;
  // Betriebskosten haben kein Projekt. Bis 25.09.2026 verschwand nur das Feld,
  // die Zuordnung blieb unsichtbar stehen.
  if (k.id === "betrieb" && beleg.projekt_id) p.projekt_id = null;
  return p;
}

/** Kategorien ohne Projektbezug — die Projektzuordnung wird dann ausgeblendet. */
export function ohneProjektbezug(beleg: Beleg): boolean {
  if (beleg.ausgabe_typ === "helfer" && beleg.helfer_rechnung_art === "pauschale") return false;
  const k = aktiveBelegKategorie(beleg)?.id;
  return k === "betrieb" || k === "helfer";
}

/* ══ Meilensteine ═══════════════════════════════════════════════════════ */

export type Meilensteine = {
  erfasst: boolean;
  /**
   * Die Zahlung ist belegt: Bankzeile zugeordnet, Barzahlung (braucht keine
   * Bankzeile) ODER privat bezahlt mit Zahlungsnachweis bei DATEV.
   */
  konto: boolean;
  /**
   * „Einlage" statt „Konto", sobald ein Inhaber privat gezahlt hat — das Geld
   * lief nie über das Geschäftskonto, eine Bankzeile gibt es dafür nicht.
   * Fertig ist dieser Schritt erst, wenn der Zahlungsnachweis bei DATEV ist.
   */
  kontoLabel: "Konto" | "Bar" | "Einlage";
  /** Erklärung für den Tooltip und den Status-Verlauf im Detail. */
  kontoTitel: string;
  datev: boolean;
  istEntwurf: boolean;
};

function einlageTitel(p: BelegPrivatZahlung): string {
  const wie = p.art === "bar" ? "Bar bezahlt" : "Privat überwiesen";
  const wer = p.zahler || "einem Inhaber";
  return p.nachweis_versendet
    ? `${wie} von ${wer} — Einlage, Zahlungsnachweis ${p.nachweis_nummer} ist bei DATEV`
    : `${wie} von ${wer} — Einlage, Zahlungsnachweis noch nicht bei DATEV (im Lohnkonto senden)`;
}

export function meilensteine(b: Beleg): Meilensteine {
  const istEntwurf = b.status === "entwurf" || b.status === "eingereicht";
  const bar = (b.zahlungsart || "") === "bar";
  const privat = !b.kontobewegung_id ? b.privat_zahlung ?? null : null;
  if (privat) {
    return {
      erfasst: !istEntwurf,
      konto: privat.nachweis_versendet,
      kontoLabel: "Einlage",
      kontoTitel: einlageTitel(privat),
      datev:
        !!b.an_steuerberater_versendet_at ||
        b.status === "an_steuerberater_gesendet" ||
        b.status === "verbucht",
      istEntwurf,
    };
  }
  // Privat ausgelegt (Einlage): das Geld lief nie über das GbR-Konto, also gibt es
  // keine Bankzeile, die noch fehlen könnte (03.11.2026).
  const ausgelegt = (b.zahlungsweg || "") === "inhaber";
  return {
    erfasst: !istEntwurf,
    konto: !!b.kontobewegung_id || bar || ausgelegt,
    kontoLabel: bar ? "Bar" : ausgelegt && !b.kontobewegung_id ? "Einlage" : "Konto",
    kontoTitel: b.kontobewegung_id
      ? "Bankzeile zugeordnet"
      : bar ? "Barzahlung — keine Bankzeile nötig"
      : ausgelegt ? "Privat ausgelegt — keine Bankzeile der GbR, als Einlage gebucht"
      : "Noch keine Bankzeile zugeordnet",
    datev:
      !!b.an_steuerberater_versendet_at ||
      b.status === "an_steuerberater_gesendet" ||
      b.status === "verbucht",
    istEntwurf,
  };
}

export type ChipId = "alle" | "offen" | "entwurf" | "datev" | "verworfen";

/**
 * Filterleiste. „An DATEV" ist bewusst KEIN Filter mehr — danach wird nicht
 * gesucht. Der Meilenstein bleibt in der Zeile sichtbar, und ueber „Alle"
 * findet man die versendeten Belege weiterhin.
 */
export const CHIPS: { id: ChipId; label: string }[] = [
  { id: "entwurf", label: "Entwurf" },
  { id: "offen", label: "Offen" },
  { id: "alle", label: "Alle" },
  // Eigener Reiter statt Ausblenden: verworfene Belege sollen niemandem im Weg
  // stehen, aber auffindbar bleiben — bei einer Pruefung ist die Frage „was ist
  // aus diesem Beleg geworden" leichter zu beantworten als „welchen Beleg gab
  // es hier mal".
  { id: "verworfen", label: "Verworfen" },
];

/** Entwurf (pruefen) · Offen (erfasst, DATEV fehlt) · An DATEV (fertig). */
export function chipOf(b: Beleg): ChipId {
  if (b.status === "verworfen") return "verworfen";
  const m = meilensteine(b);
  if (m.istEntwurf) return "entwurf";
  if (m.datev) return "datev";
  return "offen";
}

export function datevDatum(b: Beleg): string {
  const s = (b.an_steuerberater_versendet_at || "").slice(0, 10);
  return s ? fmtDate(s) : "";
}

/* ══ Formatierer ════════════════════════════════════════════════════════ */

export function euro(n: number | null | undefined): string {
  return (Number(n) || 0).toLocaleString("de-DE", { style: "currency", currency: "EUR" });
}

export function fmtDate(iso: string): string {
  const s = String(iso || "").slice(0, 10);
  const [y, m, d] = s.split("-");
  return y && m && d ? `${d}.${m}.${y}` : s || "—";
}

/** "1.234,56" / "84,20" / "84.20" → number | null (leer/ungueltig = null). */
export function parseBetrag(s: string): number | null {
  let t = (s || "").trim();
  if (t === "") return null;
  if (t.includes(",")) t = t.replace(/\./g, "").replace(",", ".");
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export function belegTypLabel(b: Beleg): string {
  if (b.beleg_typ === "lieferschein") return "Lieferschein";
  if (b.beleg_typ === "vertragsunterlage") return "Vertragsunterlage";
  return aktiveBelegKategorie(b)?.label || DOKUMENTART[b.beleg_typ || ""] || "Beleg";
}

/** Betrag fürs Eingabefeld: „320,50", leer bei 0. */
export function feldBetrag(n: number | null | undefined): string {
  const v = Number(n) || 0;
  return v ? v.toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "";
}

/* ══ Dateiadressen ══════════════════════════════════════════════════════ */
// Der Server rendert die Vorschau (JPEG) selbst — kein pdf.js im Bundle.

export function belegThumbUrl(id: string, breite = 700): string {
  return `/api/kontor/belege/${encodeURIComponent(id)}/thumbnail?w=${breite}`;
}

export function belegDateiUrl(id: string): string {
  return `/api/kontor/belege/${encodeURIComponent(id)}/pdf`;
}

/**
 * Eine einzelne Seite des Belegs als JPEG.
 *
 * Die Lightbox zeigt Bilder statt eines eingebetteten PDFs: ein `<iframe>` mit
 * PDF haengt am PDF-Handler des Browsers, und Firefox unter Linux laedt die
 * Datei bei der Voreinstellung „PDF speichern“ herunter, statt sie zu zeigen.
 * Ein `<img>` tut das nie.
 */
export function belegSeiteUrl(id: string, seite: number, breite = 1200): string {
  return `/api/kontor/belege/${encodeURIComponent(id)}/thumbnail?w=${breite}&page=${seite}`;
}

/** Eintrag der kanonischen Lieferantenliste (`lieferant_firma`). */
export type Lieferant = { id: string; name: string };
